import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { CreateUserSchema, UpdateUserSchema } from '@portal/schemas'
import type { z } from 'zod'
import type { UserWithPermissions } from '@portal/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { usersApi } from '@/lib/api/users'
import { cn } from '@/lib/utils'

type CreateValues = z.infer<typeof CreateUserSchema>
type UpdateValues = z.infer<typeof UpdateUserSchema>

const MODULES = [
  { key: 'PAYMENTS',  label: 'Payments',       description: 'Create and submit payment requests' },
  { key: 'APPROVALS', label: 'Approvals',       description: 'Approve or reject pending payments' },
  { key: 'VENDORS',   label: 'Vendors',         description: 'Manage vendor accounts' },
  { key: 'USERS',     label: 'User Management', description: 'Add and manage platform users' },
]

interface Props {
  open: boolean
  user?: UserWithPermissions | null
  onClose: () => void
  onSaved: (user: UserWithPermissions, activationUrl?: string) => void
}

export default function UserForm({ open, user, onClose, onSaved }: Props) {
  const isEdit = !!user
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [granted, setGranted] = useState<Set<string>>(new Set())

  const createForm = useForm<CreateValues>({
    resolver: zodResolver(CreateUserSchema),
    defaultValues: { role: 'USER' },
  })

  const editForm = useForm<UpdateValues>({
    resolver: zodResolver(UpdateUserSchema),
    values: user
      ? {
          name: user.name,
          phone: user.phone ?? '',
          isActive: user.isActive,
          dailyPaymentLimitRupees: user.dailyPaymentLimitPaise != null ? user.dailyPaymentLimitPaise / 100 : null,
          transactionLimitRupees: user.transactionLimitPaise != null ? user.transactionLimitPaise / 100 : null,
        }
      : undefined,
  })

  const toggleModule = (key: string) => {
    setGranted((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const onSubmit = async (values: CreateValues | UpdateValues) => {
    setError('')
    setLoading(true)
    try {
      if (isEdit && user) {
        const res = await usersApi.update(user.id, values as UpdateValues)
        if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to update user')
        onSaved(res.data)
      } else {
        const permissions = MODULES.map(({ key }) => ({ module: key, granted: granted.has(key) }))
        const res = await usersApi.create({ ...(values as CreateValues), permissions })
        if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create user')
        onSaved(res.data.user, res.data.activationUrl)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setError('')
    createForm.reset()
    setGranted(new Set())
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit User' : 'Create User'}</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <span className="text-sm">{error}</span>
          </Alert>
        )}

        {isEdit ? (
          <form onSubmit={editForm.handleSubmit(onSubmit as any)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input id="edit-name" {...editForm.register('name')} />
              {editForm.formState.errors.name && (
                <p className="text-xs text-destructive">{editForm.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone (optional)</Label>
              <Input id="edit-phone" placeholder="10-digit mobile" {...editForm.register('phone')} />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-active"
                className="h-4 w-4 rounded"
                {...editForm.register('isActive')}
              />
              <Label htmlFor="edit-active" className="font-normal cursor-pointer">Active</Label>
            </div>

            <div className="rounded-lg border p-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment Limits</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-daily-limit" className="text-xs">Daily Limit (₹)</Label>
                  <Input
                    id="edit-daily-limit"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="No limit"
                    {...editForm.register('dailyPaymentLimitRupees', { valueAsNumber: true, setValueAs: v => v === '' || isNaN(v) ? null : v })}
                  />
                  {editForm.formState.errors.dailyPaymentLimitRupees && (
                    <p className="text-xs text-destructive">{editForm.formState.errors.dailyPaymentLimitRupees.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-txn-limit" className="text-xs">Per-Transaction Limit (₹)</Label>
                  <Input
                    id="edit-txn-limit"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="No limit"
                    {...editForm.register('transactionLimitRupees', { valueAsNumber: true, setValueAs: v => v === '' || isNaN(v) ? null : v })}
                  />
                  {editForm.formState.errors.transactionLimitRupees && (
                    <p className="text-xs text-destructive">{editForm.formState.errors.transactionLimitRupees.message}</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Leave blank for unlimited. Applies to USER role only.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={createForm.handleSubmit(onSubmit as any)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-email">Email Address</Label>
              <Input id="new-email" type="email" placeholder="user@company.com" {...createForm.register('email')} />
              {createForm.formState.errors.email && (
                <p className="text-xs text-destructive">{createForm.formState.errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-name">Full Name</Label>
              <Input id="new-name" placeholder="Full name" {...createForm.register('name')} />
              {createForm.formState.errors.name && (
                <p className="text-xs text-destructive">{createForm.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-phone">Phone (optional)</Label>
              <Input id="new-phone" placeholder="10-digit mobile" {...createForm.register('phone')} />
            </div>

            {/* Module Access */}
            <div className="space-y-2">
              <Label>Module Access</Label>
              <p className="text-xs text-muted-foreground -mt-1">
                Dashboard is always available. Enable additional modules below.
              </p>
              <div className="space-y-1.5">
                {MODULES.map(({ key, label, description }) => {
                  const isGranted = granted.has(key)
                  return (
                    <div
                      key={key}
                      onClick={() => toggleModule(key)}
                      className={cn(
                        'flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2.5 transition-colors hover:bg-accent',
                        isGranted ? 'border-primary/30 bg-primary/5' : 'border-border',
                      )}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-tight">{label}</p>
                        <p className="text-xs text-muted-foreground">{description}</p>
                      </div>
                      <div
                        className={cn(
                          'ml-3 flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
                          isGranted ? 'bg-primary' : 'bg-muted',
                        )}
                      >
                        <div
                          className={cn(
                            'h-4 w-4 rounded-full bg-white shadow transition-transform mx-0.5',
                            isGranted ? 'translate-x-4' : 'translate-x-0',
                          )}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="rounded-lg border p-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment Limits</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="new-daily-limit" className="text-xs">Daily Limit (₹)</Label>
                  <Input
                    id="new-daily-limit"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="No limit"
                    {...createForm.register('dailyPaymentLimitRupees', { valueAsNumber: true, setValueAs: v => v === '' || isNaN(v) ? null : v })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-txn-limit" className="text-xs">Per-Transaction Limit (₹)</Label>
                  <Input
                    id="new-txn-limit"
                    type="number"
                    min="1"
                    step="1"
                    placeholder="No limit"
                    {...createForm.register('transactionLimitRupees', { valueAsNumber: true, setValueAs: v => v === '' || isNaN(v) ? null : v })}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Leave blank for unlimited. Applies to USER role only.</p>
            </div>

            <p className="text-xs text-muted-foreground">
              Enter the user's Microsoft work email. They can sign in immediately using "Continue with Microsoft" on the login page.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create & Send Invite
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
